import { hostHintFromFrameName, rememberEmbeddedHostHint } from '@/lib/embedded-host';

/**
 * Whether this browser tab booted as an embedded (Bitrix24 iframe) session.
 *
 * Captured ONCE at module load from the initial pathname — the exact signal
 * main.tsx uses to decide whether to mount ClerkProvider. It is intentionally a
 * constant, not a hook: the ClerkProvider decision is made once and never
 * changes for the life of the tab.
 *
 * Consequence: an embedded session has NO ClerkProvider. Any later client-side
 * navigation to a non-`/embedded` route (which RootLayout would otherwise render
 * via the Clerk-dependent MainAppLayout) must be prevented — rendering it throws
 * "useClerk can only be used within <ClerkProvider />". RootLayout uses this flag
 * to bounce such strays back into the embedded shell.
 */
export const IS_EMBEDDED_SESSION =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/embedded');

let bootstrapToken: string | null = null;

// Capture the one-time token from the fragment before Sentry, router logging, or
// any component can observe the URL. URL fragments are not sent to servers or in
// Referer headers; replaceState also removes the secret from browser history.
if (IS_EMBEDDED_SESSION) {
  const search = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  bootstrapToken = fragment.get('bootstrap');
  let shouldReplaceUrl = false;

  if (bootstrapToken) {
    fragment.delete('bootstrap');
    shouldReplaceUrl = true;
  }

  // `host=` names the CRM page that mounted the frame (#746, spec
  // 2026-10-29-uspacy-card-tab-session.md §10.2). It decides only what has to be
  // decided before the server answers — no Bitrix24 SDK, recovery by message —
  // and leaves the address with the bootstrap: it is not a screen parameter.
  // After a reload the address has no `host=`: the frame name the page set
  // (`aelo-host:<vendor>`) carries the same hint.
  rememberEmbeddedHostHint(search.get('host'), window.name);

  // Older embedded links put bearer-like values in the query string. They are
  // no longer accepted for authentication, and leaving them in the address bar
  // exposes them to monitoring/navigation telemetry before the cookie flow wins.
  for (const parameter of ['sessionId', 'bootstrap', 'host']) {
    if (search.has(parameter)) {
      search.delete(parameter);
      shouldReplaceUrl = true;
    }
  }

  if (shouldReplaceUrl) {
    const remainingSearch = search.toString();
    const remainingHash = fragment.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${remainingSearch ? `?${remainingSearch}` : ''}${remainingHash ? `#${remainingHash}` : ''}`
    );
  }
}

/** Return the captured bootstrap token once, then erase the in-memory copy. */
export function takeEmbeddedBootstrapToken(): string | null {
  const token = bootstrapToken;
  bootstrapToken = null;
  return token;
}

/**
 * The Bitrix24 frame SDK cannot initialize outside its named portal iframe. A
 * name is not enough: the plain iframe of a message host is named too
 * (`aelo-host:<vendor>`), and there is no SDK parent behind it.
 */
export function isB24SdkContextAvailable(windowName: string): boolean {
  return windowName.trim().length > 0 && hostHintFromFrameName(windowName) === null;
}
